"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import {
  PH_STATS_STRINGS,
  PhStats,
} from "@/components/tools/chem-reactions/ph-stats";
import {
  WEAK_SOLUTION_STRINGS,
  WeakSolutionPanel,
} from "@/components/tools/chem-reactions/weak-solution-panel";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  decimalsOf,
  type KnownKind,
  type SoluteSide,
  strongSolution,
  valuesFromKnown,
} from "@/lib/tool-kit/chem-reactions/ph";
import {
  fewestSigFigs,
  formatFixed,
  formatSig,
  type NumberFormatter,
  sigFigsOf,
} from "@/lib/tool-kit/chemistry/format";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const PH_CALCULATOR_STRINGS = {
  ...PH_STATS_STRINGS,
  ...WEAK_SOLUTION_STRINGS,
  modeLabel: "What do you want to work out?",
  modeConvert: "Convert pH, pOH, [H⁺] and [OH⁻]",
  modeStrong: "pH of a strong acid or base",
  modeWeak: "pH of a weak acid or base",
  knownLabel: "I know the",
  knownPh: "pH",
  knownPoh: "pOH",
  knownHydrogen: "[H⁺] (mol/L)",
  knownHydroxide: "[OH⁻] (mol/L)",
  valueLabel: "Value",
  valueHint: "Scientific notation works: 2.5e-4 or 2.5 × 10^-4.",
  results: "Results",
  enterValue: "Enter a value to see the result.",
  badValue: "Enter a number. A concentration must be greater than zero.",
  workingHeading: "The working",
  fromPh: "[H⁺] = 10^−pH = {value} mol/L",
  fromPoh: "pOH = 14.00 − pH = {value}, and [OH⁻] = 10^−pOH = {conc} mol/L",
  fromHydrogen: "pH = −log₁₀[H⁺] = −log₁₀({conc}) = {value}",
  fromHydroxide: "pOH = −log₁₀[OH⁻] = −log₁₀({conc}) = {value}",
  sumLine: "pH + pOH = 14.00, so pH = {ph} and pOH = {poh}",
  kwLine: "[H⁺] × [OH⁻] = 1.0 × 10⁻¹⁴",
  sigNote:
    "pH and pOH are shown with as many decimal places as the concentration has significant figures, the usual convention.",
  strongSideLabel: "The solute is",
  strongAcid: "A strong acid",
  strongBase: "A strong base",
  strongConcentration: "Concentration of the solute (mol/L)",
  strongIons: "H⁺ or OH⁻ released per formula unit",
  strongIonsHint:
    "1 for HCl, HNO₃, NaOH, KOH; 2 for Ca(OH)₂ and Ba(OH)₂. H₂SO₄ is strong only for its first proton.",
  strongLine: "[{ion}] = {ions} × {concentration} mol/L = {total} mol/L",
  ionH: "H⁺",
  ionOH: "OH⁻",
  strongPhLine: "pH = −log₁₀[H⁺] = {ph}",
  strongPohLine: "pOH = −log₁₀[OH⁻] = {poh}, so pH = 14.00 − pOH = {ph}",
  waterNote:
    "At this dilution water's own ions matter. Ignoring them would give pH {naive}; the exact value, which includes water, is {exact}.",
  copyPh: "Copy pH",
  copiedLabel: "Copied!",
  copyText: "pH = {ph}",
} as const;

type Strings = typeof PH_CALCULATOR_STRINGS;
type Mode = "convert" | "strong" | "weak";

const DEFAULT_SIG = 3;
const PH_DECIMALS_FALLBACK = 2;

function ConvertMode({
  strings,
  fmt,
}: {
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const [kind, setKind] = useState<KnownKind>("hydrogen");
  const [raw, setRaw] = useState("2.5e-4");
  const isP = kind === "pH" || kind === "pOH";
  const value = parseMeasuredNumber(raw);
  const values = value === null ? null : valuesFromKnown(kind, value);

  let body = <Notice>{strings.enterValue}</Notice>;
  if (raw.trim() !== "") {
    if (values === null) {
      body = <ErrorNotice>{strings.badValue}</ErrorNotice>;
    } else {
      const decimals = isP
        ? (decimalsOf(raw) ?? PH_DECIMALS_FALLBACK)
        : (sigFigsOf(raw) ?? DEFAULT_SIG);
      const sig = isP ? Math.max(decimals, 1) : decimals;
      const lines = [
        convertLine(kind, values, raw, decimals, sig, strings, fmt),
        t(strings.sumLine, {
          ph: formatFixed(fmt, values.pH, decimals),
          poh: formatFixed(fmt, values.pOH, decimals),
        }),
        strings.kwLine,
      ];
      body = (
        <>
          <PhStats
            decimals={decimals}
            fmt={fmt}
            sig={sig}
            strings={strings}
            values={values}
          />
          <Notice>{strings.sigNote}</Notice>
          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
            <div className={WORKING_BOX}>
              {lines.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          </div>
        </>
      );
    }
  }
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="ph-known"
          label={strings.knownLabel}
          onChange={setKind}
          options={[
            { value: "hydrogen", label: strings.knownHydrogen },
            { value: "hydroxide", label: strings.knownHydroxide },
            { value: "pH", label: strings.knownPh },
            { value: "pOH", label: strings.knownPoh },
          ]}
          value={kind}
        />
        <TextField
          hint={strings.valueHint}
          id="ph-value"
          label={strings.valueLabel}
          numeric
          onChange={setRaw}
          value={raw}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}

function convertLine(
  kind: KnownKind,
  values: NonNullable<ReturnType<typeof valuesFromKnown>>,
  raw: string,
  decimals: number,
  sig: number,
  strings: Strings,
  fmt: NumberFormatter
): string {
  const p = (value: number) => formatFixed(fmt, value, decimals);
  const c = (value: number) => formatSig(fmt, value, sig);
  switch (kind) {
    case "pH":
      return t(strings.fromPh, { value: c(values.hydrogen) });
    case "pOH":
      return t(strings.fromPoh, {
        value: p(values.pOH),
        conc: c(values.hydroxide),
      });
    case "hydrogen":
      return t(strings.fromHydrogen, { conc: raw.trim(), value: p(values.pH) });
    default:
      return t(strings.fromHydroxide, {
        conc: raw.trim(),
        value: p(values.pOH),
      });
  }
}

function StrongMode({
  strings,
  fmt,
}: {
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const [side, setSide] = useState<SoluteSide>("acid");
  const [concentration, setConcentration] = useState("0.050");
  const [ions, setIons] = useState("1");
  const c = parseMeasuredNumber(concentration);
  const n = parseMeasuredNumber(ions);
  const valid = c !== null && c > 0 && n !== null && n > 0;

  let body = <ErrorNotice>{strings.badValue}</ErrorNotice>;
  if (valid) {
    const solution = strongSolution(side, c, n);
    const sig = fewestSigFigs([concentration], DEFAULT_SIG);
    const ph = (value: number) => formatFixed(fmt, value, sig);
    const lines = [
      t(strings.strongLine, {
        ion: side === "acid" ? strings.ionH : strings.ionOH,
        ions: fmt(n),
        concentration: formatSig(fmt, c, sig),
        total: formatSig(fmt, solution.ionConcentration, sig),
      }),
      side === "acid"
        ? t(strings.strongPhLine, { ph: ph(solution.values.pH) })
        : t(strings.strongPohLine, {
            poh: ph(solution.values.pOH),
            ph: ph(solution.values.pH),
          }),
    ];
    body = (
      <>
        <PhStats
          decimals={sig}
          fmt={fmt}
          sig={sig}
          strings={strings}
          values={solution.values}
        />
        {solution.waterMatters ? (
          <Notice>
            {t(strings.waterNote, {
              naive: ph(solution.ignoringWater),
              exact: ph(solution.values.pH),
            })}
          </Notice>
        ) : null}
        <div className="space-y-3">
          <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
          <div className={WORKING_BOX}>
            {lines.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
        </div>
        <CopyButton
          copiedLabel={strings.copiedLabel}
          label={strings.copyPh}
          text={t(strings.copyText, { ph: ph(solution.values.pH) })}
        />
      </>
    );
  }
  return (
    <>
      <PillGroup
        label={strings.strongSideLabel}
        name="ph-strong-side"
        onChange={setSide}
        options={[
          { value: "acid", label: strings.strongAcid },
          { value: "base", label: strings.strongBase },
        ]}
        value={side}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id="ph-strong-concentration"
          label={strings.strongConcentration}
          numeric
          onChange={setConcentration}
          value={concentration}
        />
        <TextField
          hint={strings.strongIonsHint}
          id="ph-strong-ions"
          label={strings.strongIons}
          numeric
          onChange={setIons}
          value={ions}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}

export default function PhCalculatorWidget() {
  const strings = useToolUi(PH_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("convert");
  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="ph-mode"
        onChange={setMode}
        options={[
          { value: "convert", label: strings.modeConvert },
          { value: "strong", label: strings.modeStrong },
          { value: "weak", label: strings.modeWeak },
        ]}
        value={mode}
      />
      {mode === "convert" ? <ConvertMode fmt={fmt} strings={strings} /> : null}
      {mode === "strong" ? <StrongMode fmt={fmt} strings={strings} /> : null}
      {mode === "weak" ? (
        <section aria-live="polite" className="space-y-4">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <WeakSolutionPanel fmt={fmt} strings={strings} />
        </section>
      ) : null}
    </>
  );
}
